/**
 * Run-result upgrade strings: API may send `listOfUpgrades` labels that disagree with
 * `upgradeIds` / `upgradesPresent`. Prefer ids + structured data for display and exports.
 */

export const parseSemicolonUpgradePairs = (raw) => {
    if (raw == null || typeof raw !== "string") return [];

    return raw
        .split(";")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((segment) => {
            const i = segment.indexOf("=>");
            if (i === -1) return null;
            const upgradeType = segment.slice(0, i).trim();
            const value = segment.slice(i + 2).trim();
            if (!upgradeType) return null;
            return { upgradeType, value };
        })
        .filter(Boolean);
};

const isNoneLike = (v) => {
    if (v == null) return true;
    const s = String(v).trim();
    return s === "" || /^none$/i.test(s);
};

/**
 * Flatten `ecmInputMap` row `label` (string or per-section object) for a given ECM key.
 */
export const pickLabelFromEcmInputMapRow = (ecmKey, row) => {
    if (!row || typeof row !== "object") return "";
    const { label } = row;
    if (typeof label === "string" && !isNoneLike(label)) return label;
    if (label && typeof label === "object" && !Array.isArray(label)) {
        const byKey = label[ecmKey];
        if (typeof byKey === "string" && !isNoneLike(byKey)) return byKey;
        for (const v of Object.values(label)) {
            if (typeof v === "string" && !isNoneLike(v)) return v;
        }
    }
    return "";
};

const stripCsvCommas = (s) => String(s).replace(/,/g, "");

/**
 * One ECM column for parametric / community CSV rows.
 * @param {string} ecmKey
 * @param {{ listOfUpgrades?: string, upgradeIds?: string, upgradesPresent?: object }} values
 * @param {object} [ecmInputMap]
 */
export const resolveRunResultEcmDisplayCell = (ecmKey, values, ecmInputMap) => {
    const { listOfUpgrades = "", upgradeIds = "", upgradesPresent } = values || {};

    const listByType = Object.fromEntries(parseSemicolonUpgradePairs(listOfUpgrades).map((p) => [p.upgradeType, p.value]));
    const idsByType = Object.fromEntries(parseSemicolonUpgradePairs(upgradeIds).map((p) => [p.upgradeType, p.value]));

    const idForKey = idsByType[ecmKey] || upgradesPresent?.[ecmKey]?.id || null;
    const presentLabel = upgradesPresent?.[ecmKey]?.label;

    if (!isNoneLike(presentLabel)) {
        return stripCsvCommas(presentLabel);
    }

    if (idForKey && ecmInputMap) {
        const row = ecmInputMap[ecmKey]?.[idForKey];
        let mapped = pickLabelFromEcmInputMapRow(ecmKey, row);
        if (isNoneLike(mapped) && ecmKey === "spaceCooling") {
            const heatRow = ecmInputMap.spaceHeating?.[idForKey];
            mapped = pickLabelFromEcmInputMapRow("spaceHeating", heatRow);
        }
        if (!isNoneLike(mapped)) {
            return stripCsvCommas(mapped);
        }
    }

    const listVal = listByType[ecmKey];
    if (!isNoneLike(listVal)) {
        return stripCsvCommas(listVal);
    }

    if (idForKey) {
        return stripCsvCommas(idForKey);
    }

    return isNoneLike(listVal) ? "None" : stripCsvCommas(listVal);
};
